import React from "react";
import { X, AlertTriangle, Sparkles } from "lucide-react";
import { handlePrimaryCta, PRIMARY_CTA_LABEL } from "@/lib/contact";

const PROBLEMS = [
  {
    title: "Inquiries that go nowhere",
    desc: "They reach out at 9pm — excited. By morning they've moved on. By the time you reply, they've already signed up somewhere else.",
  },
  {
    title: "Same questions, every single day",
    desc: "\"How much?\" \"What times?\" \"What's included?\" — the same five messages stealing your evenings, every week.",
  },
  {
    title: "Pricing & services aren't obvious",
    desc: "Visitors can't tell what you offer or what it costs. They go quiet rather than ask — and you never even knew they were interested.",
  },
  {
    title: "Your site is a digital business card",
    desc: "It looks fine. It does nothing. There's no path from \"I'm interested\" to \"I'm signed up\" — so high-intent visitors just bounce.",
  },
];

export default function ProblemSection() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="section bg-nav-950 relative overflow-hidden"
    >
      <div className="container-x relative">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-red-400">
            <AlertTriangle className="h-3.5 w-3.5" />
            The Quiet Leak
          </span>
          <h2 className="mt-4 h-display text-4xl sm:text-5xl">
            You're doing great work.{" "}
            <span className="text-red-400">
              You're also leaking money and time.
            </span>
          </h2>
          <p className="mt-4 text-lg text-nav-300">
            Your audience exists. Your offer is real. But somewhere between
            "found you online" and "booked a session," people slip through
            the cracks — and you'll never know how many. Sound familiar?
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROBLEMS.map((p, i) => (
            <div
              key={i}
              data-testid={`problem-item-${i}`}
              className="rounded-2xl border border-nav-800 bg-nav-900/70 p-6 flex flex-col"
            >
              <span className="grid place-items-center h-9 w-9 rounded-full bg-red-500/15 border border-red-500/30 text-red-400">
                <X className="h-4 w-4" />
              </span>
              <p className="mt-4 text-base font-display font-bold text-white">
                {p.title}
              </p>
              <p className="mt-2 text-sm text-nav-400 leading-relaxed flex-1">
                {p.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl border border-brand/30 bg-brand/5 p-6 sm:p-7 flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="grid place-items-center h-12 w-12 rounded-xl bg-brand/20 border border-brand/40 text-brand shrink-0">
            <Sparkles className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <p className="font-display font-bold text-lg text-white">
              Stop chasing. Start booking.
            </p>
            <p className="text-sm text-nav-300 mt-1">
              We'll mock up a real funnel for your business — free. See
              exactly what your booking flow could look like before you commit
              to anything.
            </p>
          </div>
          <a
            href="#contact"
            onClick={handlePrimaryCta}
            data-testid="problem-primary-cta"
            className="btn-primary shrink-0 whitespace-nowrap"
          >
            <Sparkles className="h-5 w-5" />
            {PRIMARY_CTA_LABEL}
          </a>
        </div>
      </div>
    </section>
  );
}
